import type { ReactNode, Ref } from 'react';
import type { DialogProps } from '@mui/material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { SubmitButton } from './SubmitButton';

export interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Dialog title (sentence case, translated copy). */
  title: string;
  /** Optional subtitle under the title. */
  subtitle?: ReactNode;
  /** Form body (fields). */
  children: ReactNode;
  /**
   * Submit handler. When provided, the dialog body is wrapped in a `<form>` so
   * the footer submit button and Enter both submit — pass `handleSubmit(...)`
   * from React Hook Form here.
   */
  onSubmit?: () => void;
  /** Footer submit label (translated copy). Requires `onSubmit`. */
  submitLabel?: string;
  /** Footer cancel label (translated copy). */
  cancelLabel?: string;
  submitPending?: boolean;
  submitDisabled?: boolean;
  /** Custom footer; overrides the default cancel/submit buttons. */
  footer?: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  ref?: Ref<HTMLDivElement>;
}

/**
 * Centered modal for short forms (edit budget, add carrier). Title / body /
 * footer on MUI `Dialog`. Pass `onSubmit` to wire it as a form.
 */
export function FormDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  onSubmit,
  submitLabel,
  cancelLabel,
  submitPending = false,
  submitDisabled = false,
  footer,
  maxWidth = 'sm',
  ref,
}: FormDialogProps) {
  const hasDefaultFooter = !footer && (submitLabel || cancelLabel);

  const inner = (
    <>
      <DialogTitle component="div" sx={{ pr: 6 }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontWeight: 400 }}>
            {subtitle}
          </Typography>
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </DialogContent>
      {(footer || hasDefaultFooter) && (
        <DialogActions sx={{ bgcolor: 'paperAlt', p: '14px 22px' }}>
          {footer ?? (
            <>
              {cancelLabel && (
                <Button variant="outlined" color="inherit" type="button" onClick={onClose}>
                  {cancelLabel}
                </Button>
              )}
              {submitLabel && (
                <SubmitButton
                  type={onSubmit ? 'submit' : 'button'}
                  pending={submitPending}
                  disabled={submitDisabled}
                >
                  {submitLabel}
                </SubmitButton>
              )}
            </>
          )}
        </DialogActions>
      )}
    </>
  );

  return (
    <Dialog
      ref={ref}
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      slotProps={{ paper: { sx: { width: 520, maxWidth: 520, borderRadius: '8px' } } }}
    >
      {onSubmit ? (
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {inner}
        </Box>
      ) : (
        inner
      )}
    </Dialog>
  );
}
